


import React, { useEffect, useState } from 'react';
import { FaEdit, FaSave } from 'react-icons/fa';
import axios from 'axios';
import "./Settings.css";

const Billing = () => {
  const vendorId = localStorage.getItem("vendorId") || "42";

  const [selectedOptions, setSelectedOptions] = useState({});
  const [editMode, setEditMode] = useState({});
  const [billingData, setBillingData] = useState(null);

  // ✅ Initialize from API data
  const initializeSelectedOptions = (apiData) => {
    setSelectedOptions({
      serviceCharges: {
        discount: apiData.serviceCharges ?? 0,
        type: apiData.serviceChargesType === "CUSTOMER_PAYABLE" ? "user" : "no user",
        apply: apiData.serviceChargesApply || null,
        unit: "Percentage",
      },
      platformCharges: {
        type: apiData.platformChargeType === "CUSTOMER_PAYABLE" ? "user" : "no user",
        unit: "Percentage",
      }
    });
  };

  // ✅ Fetch data from backend
  const fetchBillingData = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      console.warn("⚠️ No token found in localStorage.");
      return null;
    }

    try {
      const res = await axios.get(
        `https://backend.maamaas.com/food/api/billing/get/${vendorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBillingData(res.data);
      initializeSelectedOptions(res.data);
      return res.data;
    } catch (err) {
      console.error("❌ Failed to fetch billing data:", err?.response?.data || err);
      return null;
    }
  };

  useEffect(() => {
    fetchBillingData();
  }, []);

  // ✅ Toggle Edit Mode
  const toggleEdit = async (key) => {
    const isEdit = editMode[key];
    const token = localStorage.getItem("authToken");
    const id = billingData?.id || null;

    if (isEdit && id) {
      const updatedData = {
        ...billingData,
        id,
        vendorId,

        serviceCharges: Number(selectedOptions.serviceCharges?.discount) || 0,
        serviceChargesType:
          selectedOptions.serviceCharges?.type === "user"
            ? "CUSTOMER_PAYABLE"
            : "BUSINESS_BORNE",
        serviceChargesApply: selectedOptions.serviceCharges?.apply,

        platformChargeType:
          selectedOptions.platformCharges?.type === "user"
            ? "CUSTOMER_PAYABLE"
            : "BUSINESS_BORNE",
      };

      try {
        await axios.put(
          `https://backend.maamaas.com/food/api/billing/edit/${id}`,
          updatedData,
          {
            headers: {
              "Content-Type": "application/json",
              ...(token && { Authorization: `Bearer ${token}` }),
            },
          }
        );
        alert("✅ Charges updated successfully!");
        await fetchBillingData();
      } catch (err) {
        console.error("❌ Failed to update charges:", err);
        alert("❌ Error saving changes.");
      }
    }

    setEditMode((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // ✅ Toggle apply (Applicable / Not_Applicable)
  const handleApplyChange = (label) => {
    setSelectedOptions(prev => ({
      ...prev,
      [label]: {
        ...prev[label],
        apply: prev[label]?.apply === "Applicable" ? "Not_Applicable" : "Applicable",
      },
    }));
  };

  // ✅ Handle charge type checkbox (user / no user)
  const handleOptionChange = (label, selectedType) => {
    setSelectedOptions(prev => ({
      ...prev,
      [label]: {
        ...prev[label],
        type: selectedType
      }
    }));
  };

  // ✅ Handle numeric input for service charges only
  const handleDiscountChange = (label, value) => {
    setSelectedOptions(prev => ({
      ...prev,
      [label]: {
        ...prev[label],
        discount: value
      }
    }));
  };

  // ✅ Save all changes
  const handleSaveCharges = async () => {
    const token = localStorage.getItem("authToken");

    const requestBody = {
      ...billingData,
      id: billingData?.id || 0,
      vendorId: billingData?.vendorId || vendorId,

      // Service charges (with numeric and apply)
      serviceCharges: Number(selectedOptions.serviceCharges?.discount) || 0,
      serviceChargesType:
        selectedOptions.serviceCharges?.type === "user"
          ? "CUSTOMER_PAYABLE"
          : "BUSINESS_BORNE",
      serviceChargesApply: selectedOptions.serviceCharges?.apply,

      // Platform charges (type only)
      platformChargeType:
        selectedOptions.platformCharges?.type === "user"
          ? "CUSTOMER_PAYABLE"
          : "BUSINESS_BORNE",
    };

    try {
      await axios.post(
        `https://backend.maamaas.com/food/api/billing/add/charges/${vendorId}`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );
      alert("✅ Charges saved successfully!");
    } catch (err) {
      console.error("❌ Backend error:", err?.response?.data || err);
      alert(`❌ Failed to save charges: ${err?.response?.data?.error || "Internal Server Error"}`);
    }
  };

  return (
    <div className='billingsetup' style={containerStyle}>
      <div style={{
        width: "100%",
        overflowX: "auto",   // horizontal scroll
        overflowY: "auto",   // vertical scroll
        maxHeight: "500px",  // limit height

      }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={tableHeaderStyle}>Charge Type</th>
              <th style={tableHeaderStyle}>Customer Payable</th>
              <th style={tableHeaderStyle}>Business Borne</th>
              <th style={tableHeaderStyle}>Visibility</th>
              <th style={tableHeaderStyle}>Unit</th>
              <th style={tableHeaderStyle}>Value</th>
              <th style={tableHeaderStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* ✅ Service Charges Row */}
            <tr>
              <td style={tableCellStyle}><strong>Service Charges</strong></td>

              <td style={tableCellStyle}>
                <input
                  type="checkbox"
                  checked={selectedOptions['serviceCharges']?.type === 'user'}
                  onChange={() => handleOptionChange('serviceCharges', 'user')}
                  disabled={!editMode['serviceCharges']}
                  style={{ transform: 'scale(1.2)', accentColor: '#4A148C' }}
                />
              </td>

              <td style={tableCellStyle}>
                <input
                  type="checkbox"
                  checked={selectedOptions['serviceCharges']?.type === 'no user'}
                  onChange={() => handleOptionChange('serviceCharges', 'no user')}
                  disabled={!editMode['serviceCharges']}
                  style={{ transform: 'scale(1.2)', accentColor: '#4A148C' }}
                />
              </td>

              <td style={tableCellStyle}>
                <input
                  type="checkbox"
                  checked={selectedOptions['serviceCharges']?.apply === 'Applicable'}
                  onChange={() => handleApplyChange('serviceCharges')}
                  disabled={!editMode['serviceCharges']}
                  style={{ transform: 'scale(1.2)', accentColor: '#4A148C' }}
                />
              </td>

              <td style={tableCellStyle}>Percentage</td>

              <td style={tableCellStyle}>
                {editMode['serviceCharges'] ? (
                  <input
                    type="number"
                    placeholder="Enter value"
                    value={selectedOptions['serviceCharges']?.discount || ''}
                    onChange={(e) => handleDiscountChange('serviceCharges', e.target.value)}
                    style={inputStyle}
                  />
                ) : (
                  selectedOptions['serviceCharges']?.discount || '--'
                )}
              </td>

              <td style={tableCellStyle}>
                <button
                  onClick={() => toggleEdit('serviceCharges')}
                  style={iconButtonStyle}
                >
                  {editMode['serviceCharges'] ? <FaSave /> : <FaEdit />}
                </button>
              </td>
            </tr>

            {/* ✅ Platform Charges Row (Type only) */}
            <tr>
              <td style={tableCellStyle}><strong>Platform Charges</strong></td>

              <td style={tableCellStyle}>
                <input
                  type="checkbox"
                  checked={selectedOptions['platformCharges']?.type === 'user'}
                  onChange={() => handleOptionChange('platformCharges', 'user')}
                  disabled={!editMode['platformCharges']}
                  style={{ transform: 'scale(1.2)', accentColor: '#4A148C' }}
                />
              </td>

              <td style={tableCellStyle}>
                <input
                  type="checkbox"
                  checked={selectedOptions['platformCharges']?.type === 'no user'}
                  onChange={() => handleOptionChange('platformCharges', 'no user')}
                  disabled={!editMode['platformCharges']}
                  style={{ transform: 'scale(1.2)', accentColor: '#4A148C' }}
                />
              </td>

              <td style={tableCellStyle}>--</td>
              <td style={tableCellStyle}>--</td>
              <td style={tableCellStyle}>--</td>

              <td style={tableCellStyle}>
                <button
                  onClick={() => toggleEdit('platformCharges')}
                  style={iconButtonStyle}
                >
                  {editMode['platformCharges'] ? <FaSave /> : <FaEdit />}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
        <button
          onClick={handleSaveCharges}
          style={{
            background: "rgb(177 93 198)",
            color: "#fff",
            borderRadius: "10px",
            padding: "10px 20px",
            border: "none",
            cursor: "pointer"
          }}
        >
          Save
        </button>
      </div>
    </div>
  );
};

// Styles
const containerStyle = {
  maxWidth: '900px',
  margin: '0 auto',
  padding: '0px',
  borderRadius: '12px',
  fontFamily: 'Segoe UI, sans-serif',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
};

const tableHeaderStyle = {
  border: '1px solid #ddd',
  textAlign: 'center',
  fontWeight: 'bold',
  backgroundColor: '#b15cd6',
  color: 'white',
  padding: '8px',
};

const tableCellStyle = {
  border: '1px solid #ddd',
  textAlign: 'center',
  padding: '8px',
};

const inputStyle = {
  width: '100px',
  padding: '5px',
  fontSize: '14px',
  border: 'none',
  outline: 'none',
  backgroundColor: '#f7f7f7',
};

const iconButtonStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: '18px',
  color: '#4A148C',
};

export default Billing;
